import type { User } from 'firebase/auth'

/**
 * Cache del ID token.
 *
 * `getIdToken()` de Firebase ya cachea, pero cada request del cliente HTTP lo
 * llama y eso implica un await por request. Guardamos el token con su
 * expiracion y un margen de 60s: si esta fresco, el interceptor es sincrono en
 * la practica.
 *
 * `forceRefresh` existe para un solo caso: el 401 del interceptor.
 */

export interface TokenManager {
  getToken(forceRefresh?: boolean): Promise<string | null>
  setUser(user: User | null): void
  clear(): void
}

const EXPIRY_MARGIN_MS = 60_000

interface CachedToken {
  value: string
  expiresAt: number
}

export function createTokenManager(): TokenManager {
  let user: User | null = null
  let cached: CachedToken | null = null
  let inFlight: Promise<string | null> | null = null

  const isFresh = (): boolean => Boolean(cached && cached.expiresAt - EXPIRY_MARGIN_MS > Date.now())

  const fetchToken = async (forceRefresh: boolean): Promise<string | null> => {
    if (!user) return null
    try {
      const result = await user.getIdTokenResult(forceRefresh)
      cached = {
        value: result.token,
        expiresAt: new Date(result.expirationTime).getTime(),
      }
      return result.token
    } catch {
      cached = null
      return null
    }
  }

  return {
    async getToken(forceRefresh = false) {
      if (!user) return null
      if (!forceRefresh && isFresh()) return cached!.value

      // Coalescing: 8 queries en paralelo tras un 401 no disparan 8 refreshes.
      if (!forceRefresh && inFlight) return inFlight

      const promise = fetchToken(forceRefresh).finally(() => {
        if (inFlight === promise) inFlight = null
      })
      inFlight = promise
      return promise
    },

    setUser(next) {
      user = next
      cached = null
      inFlight = null
    },

    clear() {
      user = null
      cached = null
      inFlight = null
    },
  }
}

export const tokenManager = createTokenManager()
